import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GenieLoader } from '@/components/ui/GenieLoader';
import { useActivationPacksForAgent } from '@/hooks/activation';
import { ActivationPackService } from '@/services/database/ActivationPackService';

const ActivationPackByGenieRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { agentId } = useParams<{ agentId: string }>();
  const { data: packs, isLoading } = useActivationPacksForAgent(agentId ?? '');
  const hasCreatedRef = useRef(false);

  useEffect(() => {
    if (!agentId || isLoading) return;

    if (packs && packs.length > 0) {
      navigate(`/genie-hub/activation/${packs[0].id}${location.hash}`, { replace: true, state: location.state });
      return;
    }

    if (hasCreatedRef.current) return;
    hasCreatedRef.current = true;

    (async () => {
      try {
        const pack = await ActivationPackService.createDefaultPackForGenie(agentId);
        if (!pack?.id) {
          // Never drop someone on a list with no explanation for why they
          // aren't where they asked to be.
          toast.error("We couldn't open an activation pack for that Genie", {
            description: 'Pick it from the list to try again.',
          });
          navigate('/genie-hub/activation/all', { replace: true });
          return;
        }

        queryClient.invalidateQueries({ queryKey: ['activation-packs'] });
        queryClient.invalidateQueries({ queryKey: ['activation-packs-agent', agentId] });
        navigate(`/genie-hub/activation/${pack.id}${location.hash}`, { replace: true, state: location.state });
      } catch (error) {
        console.error('Failed to open the activation pack for this Genie:', error);
        toast.error("We couldn't open an activation pack for that Genie", {
          description: error instanceof Error ? error.message : 'Please try again.',
        });
        navigate('/genie-hub/activation/all', { replace: true });
      }
    })();
  }, [agentId, isLoading, packs, navigate, queryClient, location.hash, location.state]);

  return <GenieLoader size="fullscreen" loadingText="Opening your activation pack…" />;
};

export default ActivationPackByGenieRedirect;
